import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { BrandMark } from "@/components/JourneyChrome";
import { Button } from "@/components/ui/button";
import { journeyApi } from "@/lib/journeyApi";
import { apiError } from "@/lib/journeyApi";
import {
  WelcomeStep, IncomeTypeStep, IncomeDetailsStep, EssentialsStep, LivingStep,
  ProtectionStep, DebtsStep, ReviewStep, CompleteStep,
  buildPayload, blankEssential, blankDebt, num,
} from "@/components/onboarding/OnboardingSteps";

const ORDER = ["welcome", "incomeType", "incomeDetails", "essentials", "living", "protection", "debts", "review", "complete"];
const PROGRESS_LABELS = ["Income", "Essentials", "Living Bucket", "Protection", "Debts"];
const PROGRESS_INDEX = { incomeType: 0, incomeDetails: 0, essentials: 1, living: 2, protection: 3, debts: 4 };
// Privacy-safe categorical completion events fired when advancing past a step.
const STEP_EVENT = {
  incomeDetails: "foundation_income_completed",
  essentials: "foundation_essentials_completed",
  living: "foundation_living_completed",
  protection: "foundation_protection_completed",
  debts: "foundation_debts_completed",
};

const EMPTY_FORM = {
  income: {
    type: "", take_home_paycheck: "", pay_frequency: "monthly", next_payday: "",
    income_timing_uncertain: false, income_history_inconsistent: false, no_other_household_income: false,
    variable_sources: [], regular_covers_essentials: "",
  },
  essentials: [],
  living_bucket: "",
  safety: { emergency_savings: "", protected_runway: "" },
  mortgage: { has_mortgage: false, balance: "" },
  debts: [],
};

export default function Onboarding() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [screen, setScreen] = useState("welcome");
  const [form, setForm] = useState(EMPTY_FORM);
  const [ready, setReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const formRef = useRef(form);
  formRef.current = form;
  const hydrated = useRef(false);

  // Load any server-side draft (resume) or bounce to dashboard if already done.
  useEffect(() => {
    let cancelled = false;
    journeyApi.onboardingState().then((s) => {
      if (cancelled) return;
      if (s.onboarding_complete) {
        navigate("/dashboard", { replace: true });
        return;
      }
      const d = s.draft;
      if (d?.data && Object.keys(d.data).length) {
        setForm({ ...EMPTY_FORM, ...d.data, income: { ...EMPTY_FORM.income, ...(d.data.income || {}) }, safety: { ...EMPTY_FORM.safety, ...(d.data.safety || {}) }, mortgage: { ...EMPTY_FORM.mortgage, ...(d.data.mortgage || {}) } });
        if (d.screen && d.screen !== "complete" && ORDER.includes(d.screen)) setScreen(d.screen);
      }
      hydrated.current = true;
      setReady(true);
    }).catch(() => { hydrated.current = true; setReady(true); });
    return () => { cancelled = true; };
  }, [navigate]);

  // Autosave the draft whenever the screen changes (after hydration).
  useEffect(() => {
    if (!hydrated.current || screen === "welcome" || screen === "complete") return;
    journeyApi.saveDraft({ screen, data: formRef.current }).catch(() => {});
  }, [screen]);

  // Debounced autosave on in-step field changes so a mid-step reload keeps data.
  useEffect(() => {
    if (!hydrated.current || screen === "welcome" || screen === "complete") return;
    const t = setTimeout(() => {
      journeyApi.saveDraft({ screen, data: formRef.current }).catch(() => {});
    }, 600);
    return () => clearTimeout(t);
  }, [form, screen]);

  useEffect(() => { window.scrollTo({ top: 0, behavior: "auto" }); }, [screen]);

  // Handlers
  const setIncome = useCallback((patch) => setForm((f) => ({ ...f, income: { ...f.income, ...patch } })), []);
  const setSafety = useCallback((patch) => setForm((f) => ({ ...f, safety: { ...f.safety, ...patch } })), []);
  const setLiving = useCallback((v) => setForm((f) => ({ ...f, living_bucket: v })), []);
  const setMortgage = useCallback((patch) => setForm((f) => ({ ...f, mortgage: { ...(f.mortgage || {}), ...patch } })), []);
  const addEssential = useCallback(() => setForm((f) => ({ ...f, essentials: [...f.essentials, blankEssential()] })), []);
  const updateEssential = useCallback((id, patch) => setForm((f) => ({ ...f, essentials: f.essentials.map((e) => (e.id === id ? { ...e, ...patch } : e)) })), []);
  const removeEssential = useCallback((id) => setForm((f) => ({ ...f, essentials: f.essentials.filter((e) => e.id !== id) })), []);
  const addDebt = useCallback(() => setForm((f) => ({ ...f, debts: [...f.debts, blankDebt()] })), []);
  const updateDebt = useCallback((id, patch) => setForm((f) => ({ ...f, debts: f.debts.map((d) => (d.id === id ? { ...d, ...patch } : d)) })), []);
  const removeDebt = useCallback((id) => setForm((f) => ({ ...f, debts: f.debts.filter((d) => d.id !== id) })), []);

  const idx = ORDER.indexOf(screen);
  const goTo = (s) => setScreen(s);
  const next = () => setScreen(ORDER[Math.min(idx + 1, ORDER.length - 1)]);
  const back = () => setScreen(ORDER[Math.max(idx - 1, 0)]);

  const canAdvance = () => {
    if (screen === "incomeType") return !!form.income.type;
    if (screen === "incomeDetails") {
      if (form.income.type === "variable") return true;
      return num(form.income.take_home_paycheck) > 0 && !!form.income.pay_frequency;
    }
    return true;
  };

  const onNext = () => {
    if (!canAdvance()) {
      if (screen === "incomeType") toast.error("Pick how your income shows up.");
      else toast.error("Enter your take-home amount and pay frequency.");
      return;
    }
    const ev = STEP_EVENT[screen];
    if (ev) journeyApi.trackEvent(ev).catch(() => {});
    next();
  };

  const onLock = async () => {
    setSaving(true);
    try {
      await journeyApi.completeOnboarding(buildPayload(form));
      await refresh();
      setScreen("complete");
    } catch (e) {
      toast.error(apiError(e, "Could not save your foundation. Please try again."));
    } finally {
      setSaving(false);
    }
  };

  const doSignOut = async () => { await logout(); navigate("/login", { replace: true }); };

  if (!ready) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FAF8F4]" data-testid="onboarding-loading">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#E5E0D9] border-t-[#2F6B57]" />
      </div>
    );
  }

  const showProgress = screen in PROGRESS_INDEX;
  const showFooterNav = screen !== "welcome" && screen !== "complete";
  const firstName = user?.first_name || "there";

  return (
    <div className="flex min-h-screen flex-col bg-[#FAF8F4] text-[#171717]" data-testid="onboarding-page">
      <header className="flex h-16 shrink-0 items-center justify-between border-b border-[#E5E0D9] px-5 sm:px-8">
        <BrandMark />
        <button
          type="button"
          onClick={doSignOut}
          data-testid="logout-button"
          className="inline-flex items-center gap-1.5 rounded-full pe-surface px-3.5 py-2 text-sm font-medium text-[#525252] transition-colors hover:bg-[#F5F2EC]"
        >
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </header>

      <main className="mx-auto flex w-full max-w-2xl flex-1 flex-col px-5 py-8 sm:py-12">
        {showProgress && (
          <div className="mb-8" data-testid="onboarding-progress">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#70584A]">
              Step {PROGRESS_INDEX[screen] + 1} of {PROGRESS_LABELS.length} · {PROGRESS_LABELS[PROGRESS_INDEX[screen]]}
            </p>
            <p className="mt-1 text-xs text-[#A89A88]">You're building the foundation for your first Edit.</p>
            <div className="mt-2 flex gap-1.5">
              {PROGRESS_LABELS.map((s, i) => (
                <div key={s} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= PROGRESS_INDEX[screen] ? "bg-[#2F6B57]" : "bg-[#E5E0D9]"}`} />
              ))}
            </div>
          </div>
        )}

        <div className="fade-up flex-1">
          {screen === "welcome" && <WelcomeStep firstName={firstName} onStart={next} />}
          {screen === "incomeType" && <IncomeTypeStep form={form} setIncome={setIncome} />}
          {screen === "incomeDetails" && <IncomeDetailsStep form={form} setIncome={setIncome} />}
          {screen === "essentials" && <EssentialsStep form={form} addEssential={addEssential} updateEssential={updateEssential} removeEssential={removeEssential} setMortgage={setMortgage} />}
          {screen === "living" && <LivingStep form={form} setLiving={setLiving} />}
          {screen === "protection" && <ProtectionStep form={form} setIncome={setIncome} setSafety={setSafety} />}
          {screen === "debts" && <DebtsStep form={form} addDebt={addDebt} updateDebt={updateDebt} removeDebt={removeDebt} onNoDebt={onNext} />}
          {screen === "review" && <ReviewStep form={form} goTo={goTo} onLock={onLock} saving={saving} />}
          {screen === "complete" && <CompleteStep onYes={() => navigate("/dashboard/money-landed", { replace: true })} onNot={() => navigate("/dashboard", { replace: true })} />}
        </div>

        {showFooterNav && (
          <div className="mt-8 flex items-center justify-between border-t border-[#E5E0D9] pt-5">
            <Button variant="ghost" data-testid="onboarding-back-button" onClick={back} className="min-h-11 rounded-xl text-[#70584A] hover:bg-[#F5F2EC]">
              <ArrowLeft className="mr-1 h-4 w-4" /> Back
            </Button>
            {screen !== "review" && (
              <Button data-testid="onboarding-next-button" onClick={onNext} className="min-h-11 rounded-xl bg-[#2F6B57] px-6 font-semibold text-white hover:bg-[#255643]">
                Next <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
